import { AgenticFlowByline } from '@agentic-flow-studio/shared/components/agentic-flow-byline';
import { Alert, AlertDescription } from '@agentic-flow-studio/shared/ui/alert';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { PasswordInput } from '@agentic-flow-studio/shared/ui/password-input';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { PublicBrand } from '@/components/public-brand';
import { authClient } from '@/utils/betterAuth';

const resetPasswordSchema = z
  .object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  // Better Auth's own /reset-password/:token backend endpoint validates the token from the
  // email link first, then redirects here with either ?token=<verified> or ?error=INVALID_TOKEN
  // — this page never sees the raw emailed token directly.
  const token = searchParams.get('token');
  const tokenError = searchParams.get('error');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormValues) => {
    if (!token) return;
    setError(null);
    setIsLoading(true);

    try {
      const { error: resetError } = await authClient.resetPassword({
        newPassword: data.password,
        token,
      });

      if (resetError) {
        setError(resetError.message || 'Failed to reset password. Please try again.');
        return;
      }

      setDone(true);
    } catch (err: unknown) {
      console.error('Reset password error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const invalidLink = !token || !!tokenError;

  return (
    <main className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          <PublicBrand />
          <div className="space-y-8 bg-card p-8 rounded-xl border border-border shadow-sm">
            {done ? (
              <div className="text-center space-y-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Password updated</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Your password has been reset. Sign in with your new password.
                  </p>
                </div>
                <Button className="w-full mt-4" onClick={() => navigate('/login')}>
                  Back to login
                </Button>
              </div>
            ) : invalidLink ? (
              <div className="text-center space-y-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This reset link is invalid or has expired. Request a new one to continue.
                  </AlertDescription>
                </Alert>
                <Button className="w-full" onClick={() => navigate('/forgot-password')}>
                  Request a new link
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <h2 className="text-2xl font-bold tracking-tight">Set a new password</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Choose a new password for your account
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium">
                      New password
                    </label>
                    <PasswordInput id="password" {...register('password')} />
                    {errors.password && (
                      <p className="text-xs text-destructive">{errors.password.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="confirmPassword" className="text-sm font-medium">
                      Confirm new password
                    </label>
                    <PasswordInput id="confirmPassword" {...register('confirmPassword')} />
                    {errors.confirmPassword && (
                      <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
                    )}
                  </div>
                  <Button className="w-full mt-4" type="submit" disabled={isLoading}>
                    {isLoading ? 'Updating...' : 'Update password'}
                  </Button>
                </form>
              </>
            )}
          </div>
          <p className="text-center">
            <AgenticFlowByline />
          </p>
        </div>
      </div>
    </main>
  );
}
